import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './CreditsMeter.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface CreditsMeterProps {
  /** Credits spent out of the monthly allocation. */
  used: number;
  /** The monthly allocation. Top-ups are NOT part of it — they survive the reset. */
  total: number;
  /** Purchased top-up credits, shown as "+N add-on" when there are any. */
  topup?: number;
  loading?: boolean;
  theme?: HeaderTheme;
  className?: string;
}

/** 10000 → "10,000". */
function formatCredits(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Credits readout at the top of the account menu. The bar counts UP as credits are spent, matching
 * `AccountBalanceSection` — the meter reads "how much of this month is gone", not "how much is
 * left", so a fresh cycle starts empty rather than looking maxed out.
 */
export function CreditsMeter({
  used,
  total,
  topup = 0,
  loading = false,
  theme: themeProp,
  className,
}: CreditsMeterProps) {
  const theme = useV15Theme(themeProp);
  const hasData = !loading && total > 0;
  const clampedUsed = hasData ? Math.min(Math.max(used, 0), total) : 0;
  const percent = hasData ? Math.round((clampedUsed / total) * 100) : 0;

  return (
    <div className={cn(themes[theme], styles.meter, className)}>
      <div className={styles.head}>
        <span className={styles.title}>Credits</span>
        <span className={styles.percent}>{hasData ? `${percent}%` : '—'}</span>
      </div>

      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${percent}%` }} />
      </div>

      <div className={styles.foot}>
        <span className={styles.used}>
          {hasData ? `${formatCredits(clampedUsed)} / ${formatCredits(total)} used` : '—'}
        </span>
        {hasData && topup > 0 ? (
          <span className={styles.topup}>+{formatCredits(topup)} add-on</span>
        ) : null}
      </div>
    </div>
  );
}
